import { useLazyGetSessionsAnalyticsActivityQuery } from '@carboncopyco/data';
import type { ResourceType, TimePeriod } from '@carboncopyco/types';
import {
  getDateFromDaysAgo,
  getNowInFormat,
  getTimezoneName,
} from '@carboncopyco/utils';
import { useEffect, useRef } from 'react';
import { calculatePercentageDifference, extractDataForChart } from '../Charts';

type useActivityOverviewDataProps = {
  workspaceId: string;
  assetId: string;
  resourceTypes: ResourceType[];
  timePeriod: TimePeriod;
  organizationId: string;
};

const getAssetId = (resourceType: ResourceType, assetId: string) => {
  if (resourceType === 'tour') {
    return { tourId: assetId };
  }
  if (resourceType === 'play') {
    return { playbookId: assetId };
  }
  return { demoId: assetId };
};

export function useActivityOverviewData({
  workspaceId,
  resourceTypes,
  timePeriod,
  organizationId,
  assetId,
}: useActivityOverviewDataProps) {
  const resourceTypesRef = useRef(resourceTypes);
  const [
    getChartsData,
    {
      data: chartData,
      isFetching: isFetchingChartsData,
      isError: isErrorChartsData,
    },
  ] = useLazyGetSessionsAnalyticsActivityQuery();
  const [getKpiData, { data: nowData }] =
    useLazyGetSessionsAnalyticsActivityQuery();
  const [getKpiPrevData, { data: prevData, isFetching: isFetchingPrevData }] =
    useLazyGetSessionsAnalyticsActivityQuery();

  useEffect(() => {
    getKpiData({
      organizationId,
      workspaceIds: [workspaceId],
      resourceTypes: resourceTypesRef.current,
      ...getAssetId(resourceTypesRef.current[0], assetId),
      start: getDateFromDaysAgo(Number(timePeriod)),
      end: getNowInFormat('YYYY-MM-DDTHH:mm:ssZ'),
      timezone: getTimezoneName(),
      resolution: 'summary',
      shortSessions: true,
      internalSessions: true,
    });

    getChartsData({
      organizationId,
      resourceTypes: resourceTypesRef.current,
      ...getAssetId(resourceTypesRef.current[0], assetId),
      workspaceIds: [workspaceId],
      start: getDateFromDaysAgo(Number(timePeriod)),
      end: getNowInFormat('YYYY-MM-DDTHH:mm:ssZ'),
      timezone: getTimezoneName(),
      resolution: 'day',
      shortSessions: true,
      internalSessions: true,
    });
  }, [
    workspaceId,
    timePeriod,
    assetId,
    organizationId,
    getKpiData,
    getChartsData,
  ]);

  useEffect(() => {
    getKpiPrevData({
      workspaceIds: [workspaceId],
      organizationId,
      ...getAssetId(resourceTypesRef.current[0], assetId),
      resolution: 'summary',
      resourceTypes: resourceTypesRef.current,
      end: getDateFromDaysAgo(Number(timePeriod), 1),
      start: getDateFromDaysAgo(Number(timePeriod) * 2, 1),
      timezone: getTimezoneName(),
      shortSessions: true,
      internalSessions: true,
    });
  }, [assetId, workspaceId, getKpiPrevData, organizationId, timePeriod]);

  const getPercentages = (type: 'avgDuration' | 'occurrences') => {
    const nowValuesByType = nowData?.results?.[0]?.values?.[type];
    const prevValuesByType = prevData?.results?.[0]?.values?.[type];
    if (nowValuesByType && prevValuesByType) {
      const percentagesByType = {
        [type]: calculatePercentageDifference(
          nowValuesByType ?? 0,
          prevValuesByType ?? 0
        ),
      };
      return percentagesByType[type];
    }
    return 0;
  };

  const getDescription = (type: 'avgDuration' | 'occurrences') => {
    if (!prevData?.results?.length) {
      return '';
    }
    if (getPercentages(type) === 0) {
      return `No change from previous ${timePeriod} days`;
    }
    if (!getPercentages(type)) {
      return 'No activity in the previous period';
    }
    return 'from last period';
  };

  return {
    chartData: extractDataForChart(chartData?.results),
    nowData,
    prevData,
    isLoading: isFetchingChartsData,
    isError: isErrorChartsData,
    isLoadingPrevData: isFetchingPrevData,
    getPercentages,
    getDescription,
  };
}
